import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Image, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { SpacesAsGrid } from "../../../vendor/my-spaces/components/spaces-as-grid.component";
import { Space } from "../../../vendor/my-spaces/models/space.model";
import { useSpacesApi } from "../../../../common/hooks/use-spaces-api.hook";

export const VendorSpacesEditModal = ({ vendor, setVendor, isModalOpen, setIsModalOpen }) => {
    const { t } = useTranslation();
    const [spaces, setSpaces] = useState(null);
    const [filteredSpaces, setFilteredSpaces] = useState(spaces);
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [searchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [maxSpacesTouched, setMaxSpacesTouched] = useState(false);
    const spacesAPI = useSpacesApi();

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (spaces && searchQuery) {
            const filtered = spaces.filter((space: Space) =>
                space.name?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredSpaces(filtered);
        } else {
            setFilteredSpaces(spaces);
        }
    }, [searchQuery, spaces]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Scope to this vendor's visible pool (public + associated).
            // Backend already strips visitor uploads and tags isAssigned
            // for the spaces currently in vendor.spaces[].
            const allSpaces = await spacesAPI.findAllSpaces(undefined, undefined, vendor?._id);
            const list = allSpaces?.data ?? [];
            setSpaces(list);
            setFilteredSpaces(list);
            // Preselect already-selected spaces using the backend's isAssigned tag.
            // Falls back to vendor.spaces (which may be populated objects) for
            // legacy callers that haven't refreshed since the API change.
            const preselected = list.filter((s: Space) => (s as any).isAssigned);
            setSelectedSpaces(preselected.length > 0 ? preselected : (vendor.spaces ?? []));
        } catch (error) {
            console.error("Error fetching spaces:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        const { value } = event.target;
        setMaxSpacesTouched(true);
        // Convert to number for numeric fields
        setVendor({ ...vendor, maxSpaces: value === "" ? "" : parseInt(value, 10) });
    };

    const handleSave = () => {
        setVendor({
            ...vendor,
            spaces: selectedSpaces,
        });
        setIsModalOpen(false);
    };

    return (
        <Dialog
            open={isModalOpen}
            onOpenChange={(open) => {
                if (!open) setIsModalOpen(false);
            }}
        >
            <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
                <DialogHeader className="border-b p-6 text-start">
                    <DialogTitle className="font-heading text-lg font-medium tracking-tight">
                        {t("ADMIN_VENDOREDIT.SPACES_MODAL.TITLE")}
                    </DialogTitle>
                    <DialogDescription className="text-[13px]">
                        {vendor.name
                            ? t("ADMIN_VENDOREDIT.SPACES_MODAL.SUBTITLE", { name: vendor.name })
                            : t("ADMIN_VENDOREDIT.SPACES_MODAL.SUBTITLE_FALLBACK")}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6">
                    <div className="flex flex-col gap-4">
                        {/* Max spaces configuration */}
                        <div className="grid grid-cols-1 items-center gap-4 rounded-lg border p-4 md:grid-cols-3">
                            <div>
                                <Label htmlFor="vendor-spaces-modal-max-spaces" className="mb-1.5 text-xs font-medium text-muted-foreground">
                                    {t("ADMIN_VENDOREDIT.CATALOG.MAX_SPACES")} *
                                </Label>
                                <Input
                                    id="vendor-spaces-modal-max-spaces"
                                    aria-invalid={(maxSpacesTouched && !vendor.maxSpaces) || undefined}
                                    name="maxSpaces"
                                    placeholder="3"
                                    value={vendor?.maxSpaces ?? ""}
                                    onChange={handleChange}
                                    type="number"
                                    min={1}
                                    required
                                    className="bg-background"
                                />
                            </div>
                            <p className="text-[13px] leading-relaxed text-muted-foreground md:col-span-2">
                                {t("ADMIN_VENDOREDIT.SPACES_MODAL.MAX_HINT")}{" "}
                                <span className="text-foreground tabular-nums">
                                    {t("ADMIN_VENDOREDIT.SPACES_MODAL.SELECTED_OF", {
                                        selected: selectedSpaces.filter((s) => !s.uploadedByVisitor).length,
                                        max: vendor?.maxSpaces ?? 3,
                                    })}
                                </span>
                            </p>
                        </div>

                        {/* Spaces grid */}
                        <div className="max-h-[50vh] min-h-[400px] overflow-y-auto rounded-lg border p-4">
                            {loading ? (
                                <div className="flex min-h-[360px] flex-col items-center justify-center gap-3">
                                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                                    <p className="text-sm text-muted-foreground">{t("ADMIN_VENDOREDIT.SPACES_MODAL.LOADING")}</p>
                                </div>
                            ) : filteredSpaces && filteredSpaces.length > 0 ? (
                                <SpacesAsGrid
                                    spaces={filteredSpaces}
                                    maxSpaces={vendor?.maxSpaces ?? 3}
                                    selectedSpaces={selectedSpaces}
                                    setSelectedSpaces={setSelectedSpaces}
                                    filteredSpaces={filteredSpaces}
                                />
                            ) : (
                                <div className="flex min-h-[360px] flex-col items-center justify-center gap-2">
                                    <Image className="size-8 text-muted-foreground/50" />
                                    <p className="text-sm font-medium">{t("ADMIN_VENDOREDIT.SPACES_MODAL.EMPTY_TITLE")}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {searchQuery
                                            ? t("ADMIN_VENDOREDIT.SPACES_MODAL.EMPTY_SEARCH")
                                            : t("ADMIN_VENDOREDIT.SPACES_MODAL.EMPTY_NONE")}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t p-4">
                    <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                        {t("ADMIN_SHELL.COMMON.CANCEL")}
                    </Button>
                    <Button onClick={handleSave} disabled={selectedSpaces.length === 0}>
                        <Check className="size-4" />
                        {t("ADMIN_VENDOREDIT.SPACES_MODAL.SAVE_SELECTION", { count: selectedSpaces.length })}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
